import { CircularProgress } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import CornDensityChart from '/@/components/corn-density-chart';
import { DensityTable } from '/@/components/product-datasheet';
import Title from '/@/components/title';
import useUnitContext from '/@/core/context/unit';
import settings from '/@/core/settings';
import { useDensitiesForProduct } from '/@/hooks/product-datasheet';
import { useCornDensityCurveForProducts, useYieldByEnvironmentForProducts } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { addProducttoPreview as addProducttoPreviewAction } from '/@/store/product-datasheet/actions';
import { getCompletePreview } from '/@/store/product-datasheet/selectors';
import { findClosestDensity } from '/@/utils';
import Input from '/@/v3/components/forms/input';
import styles from '../../../views/product-datasheet/preview/styles.module.css';

const DensityModule = ({
    profile,
    productDatasheet,
    userEstimatedYield,
    changeUserDensity,
    brand,
    handleDensityInput,
    userDensity,
    showLineOnChart,
}) => {
    const dispatch = useDispatch();
    const addProducttoPreview = bindActionCreators(addProducttoPreviewAction, dispatch);
    const [product, setProduct] = useState(profile);
    const productPreview = useSelector(getCompletePreview);
    const { data: cornDensityCurveQuery, isValidating: isValidatingCornDensityCurveQuery } =
        useCornDensityCurveForProducts({
            productsId: [profile.id],
            environment: productDatasheet.selectedEnvironmentRanges?.[profile.id],
            minValue: 3,
            maxValue: 12,
            step: 0.05,
            crop: productDatasheet.type,
        });
    const { data: yieldByEnvironmentQuery, isValidating: isValidatingYieldByEnvironmentQuery } =
        useYieldByEnvironmentForProducts({
            environment: productDatasheet.selectedEnvironmentRanges?.[profile.id],
            productsId: [profile.id],
            harvestDensity: userDensity?.userDensity / 10000,
        });
    const { data: densities, isValidating: isValidatingDensities } = useDensitiesForProduct({
        id: profile.id,
        crop: productDatasheet.type,
    });

    const getProductsWithAPIData = () => {
        const cornDensityCurveQueryRecommended = cornDensityCurveQuery?.[profile.id]?.recommended;
        const differential = cornDensityCurveQueryRecommended?.yield - yieldByEnvironmentQuery?.[profile.id]?.yield;
        return {
            ...profile,
            densities,
            ...(productDatasheet.type === 'corn' && {
                harvestDensity: {
                    recommended:
                        cornDensityCurveQueryRecommended?.density && cornDensityCurveQueryRecommended?.density * 10000,
                    user: userDensity?.userDensity,
                },
                estimatedYield: {
                    forRecommendedHarvestDensity: cornDensityCurveQueryRecommended?.yield,
                    forUserHarvestDensity: yieldByEnvironmentQuery?.[profile.id]?.yield,
                    differentials: {
                        ...(isNaN(differential)
                            ? {}
                            : {
                                  forUserHarvestDensity: {
                                      value: differential,
                                      percentage: 100 * (differential / cornDensityCurveQueryRecommended?.yield),
                                  },
                              }),
                    },
                },
                estimatedYieldByHarvestDensity: cornDensityCurveQuery?.[profile.id]?.points.map((p) => ({
                    estimatedYield: p.yield,
                    density: p.density * 10000,
                })),
            }),
        };
    };

    useEffect(() => {
        const data = getProductsWithAPIData();
        setProduct(data);
        if (!isValidatingCornDensityCurveQuery && !isValidatingYieldByEnvironmentQuery && !isValidatingDensities) {
            addProducttoPreview([...productPreview, data]);
        }
    }, [cornDensityCurveQuery, yieldByEnvironmentQuery, densities]);

    useEffect(() => {
        setUserDensityOnChart(userEstimatedYield);
    }, [userEstimatedYield]);

    const hasDensityGraphic = (profile) => (profile.environmentRanges || []).length > 0;
    const units = useUnitContext();
    const hasDensityTable = (profile) => !!profile.densityTableConstraints?.length;

    const hasDensity = (profile, type) => {
        if (productDatasheet.type !== type) return false;
        if (!productDatasheet.modulesIds.includes('densities')) return false;
        if (!hasDensityGraphic(profile)) return false;
        return true;
    };

    const environmentRangeId = productDatasheet.selectedEnvironmentRanges?.[profile.id];
    const environmentRange = product.environmentRanges?.find((el) => el.referenceValue === environmentRangeId);

    const setUserDensityOnChart = (userEstimatedYield?: number) => {
        if (userEstimatedYield) {
            const userInputDensity = findClosestDensity(
                units.convertYieldToKilograms(userEstimatedYield, 'corn', 4),
                product.estimatedYieldByHarvestDensity
            );
            product.harvestDensity.minEnvironmentForRecommended = null;
            product.harvestDensity.maxEnvironmentForRecommended = null;
            product.harvestDensity.recommended = userInputDensity.density;
            product.estimatedYield.forRecommendedHarvestDensity = userInputDensity.estimatedYield;
            changeUserDensity({ showLineOnChart: true, userDensity: userInputDensity.density });
        }
    };

    if (!product.id) return <CircularProgress />;
    return (
        <div className={styles.section}>
            {hasDensity(product, 'soy') &&
                hasDensityTable(product) &&
                !isValidatingDensities &&
                !!product.densities &&
                !!product.densities.length && (
                    <div className={styles.section}>
                        <Title size='xl' underline>
                            {_('Densidad')}
                        </Title>
                        <DensityTable profile={product} type={productDatasheet.type} brand={brand} />
                    </div>
                )}
            {hasDensity(product, 'corn') && hasDensityGraphic(product) && (
                <div>
                    <Title size='xl' underline className={styles.title}>
                        {_('Rendimiento Estimado')}
                    </Title>
                    <div style={{ maxWidth: '1000px' }}>
                        <CornDensityChart
                            importantReferences={showLineOnChart}
                            importantDots={showLineOnChart}
                            products={[product]}
                        />
                    </div>
                    <div className={styles.configuration}>
                        <p>{_('Manejo habitual del productor')}:</p>
                        <div className={styles.field}>
                            <div className={styles.label}>{_('Rendimiento esperado')}</div>
                            <div className={styles.input}>
                                <Input
                                    type='number'
                                    color={settings.app.theme}
                                    className={styles.input}
                                    suffix={units.getUnit('yield', 'corn')}
                                    value={userEstimatedYield ?? userEstimatedYield}
                                    onChange={handleDensityInput}
                                    label={_('Ingrese un valor')}
                                />
                            </div>
                            <div className={styles.label}>{_('Densidad estimada')}</div>
                            <div className={styles.input}>
                                <Input
                                    className={styles.estimatedYield}
                                    suffix={units.getUnit('density', 'corn')}
                                    disabled
                                    shadow
                                    value={
                                        userDensity
                                            ? units.toDensity(Math.round(userDensity?.userDensity), 'corn').localeValue
                                            : '---'
                                    }
                                />
                            </div>
                        </div>
                    </div>
                    {hasDensityTable(product) && !isValidatingDensities && !!product.densities && (
                        <div style={{ marginTop: '5rem' }}>
                            <Title size='xl' underline>
                                {_('Rangos de Densidad')}
                            </Title>
                            <DensityTable
                                type={productDatasheet.type}
                                brand={brand}
                                profile={product}
                                environmentRange={environmentRange}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default DensityModule;
